import React, { useCallback, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Toast from '../../../components/support/Toast';
import CompanyDataExport from '../../../components/company-data-export/CompanyDataExport';
import { autoDownloadExport } from '../../../components/company-data-export/exportDownload';
import BackupStatusCard from '../../../components/company-data-export/BackupStatusCard';
import CompanyClosureSection from '../../../components/company-closure/CompanyClosureSection';
import { EXPORT_ANCHOR_ID } from '../../../components/company-closure/companyClosure';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import RoleGuard from '../../../components/guards/RoleGuard';
import AccessDenied from '../../../components/access-denied/AccessDenied';
import ExportLockedPreview from './ExportLockedPreview';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';

const Export = () => {
    // O histórico de exportações e o gatilho de recarga são compartilhados entre
    // o card "Exportar meus dados" e o diálogo "Encerrar conta" (passo Exportar),
    // que inicia a exportação ali mesmo e acompanha o job pelo histórico.
    const [exportHistory, setExportHistory] = useState([]);
    const [exportRefreshKey, setExportRefreshKey] = useState(0);
    const [toast, setToast] = useState(null);
    // Exportações criadas nesta tela (card ou diálogo) baixam sozinhas ao
    // terminar: o histórico acompanha o job e avisa por onJobFinished.
    const autoDownloadIdsRef = useRef(new Set());

    const handleExportCreated = useCallback((exportId) => {
        autoDownloadIdsRef.current.add(String(exportId));
        setExportRefreshKey((key) => key + 1);
    }, []);

    const handleJobFinished = useCallback((job) => {
        const key = String(job.id);
        if (!autoDownloadIdsRef.current.has(key)) return;
        autoDownloadIdsRef.current.delete(key);
        // Erro ou cancelamento: o histórico e o diálogo já mostram o motivo.
        if (job.status !== 'complete') return;
        const files = autoDownloadExport(job);
        if (files === 0) return;
        setToast(files > 1
            ? 'Exportação concluída. A parte 1 começou a baixar; as demais estão no histórico.'
            : 'Exportação concluída. O download começou.');
    }, []);

    // Backup e dados é da pessoa responsável pela conta (papeis.md, G6): a API nega
    // o resto (403 ROLE_DENIED), e a tela explica em vez de disparar as chamadas.
    return (
        <RoleGuard
            access="dataExport"
            fallback={(
                <AccessDenied
                    title="Backup e dados é de quem é proprietário(a)"
                    description="Exportação completa, histórico das cópias e encerramento da conta ficam só com quem é proprietário(a)."
                    hint="Se precisar de uma cópia dos dados, peça a quem é proprietário(a) da conta."
                    preview={<ExportLockedPreview />}
                />
            )}
        >
        <div className='page-content'>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ px: 2, pt: 1 }}>
                    <AreaTitle
                        Title="Backup e dados"
                        Description="Suas informações ficam protegidas com cópias diárias. Exporte tudo o que é da clínica quando quiser."
                        Icon={<BackupOutlinedIcon />}
                    />
                </Box>

                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <BackupStatusCard />
                    <Box id={EXPORT_ANCHOR_ID} sx={{ scrollMarginTop: '16px' }}>
                        <CompanyDataExport
                            refreshKey={exportRefreshKey}
                            onHistoryUpdate={setExportHistory}
                            onExportCreated={handleExportCreated}
                            onJobFinished={handleJobFinished}
                        />
                    </Box>
                    <CompanyClosureSection exportHistory={exportHistory} onExportCreated={handleExportCreated} />
                </Box>
            </Box>
            <Toast open={!!toast} message={toast || ''} severity="success" onClose={() => setToast(null)} />
        </div>
        </RoleGuard>
    );
};

export default Export;
